import React, { useRef } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { ConfluenceIcon } from '@atlaskit/logo';
import { Box, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { AnyAri } from '@atlassian/ari/any-ari';
import type {
	SearchLinkedResult,
	SearchLinkedResultCategory,
} from '@atlassian/search-client/common/types';

import { products1P } from '../../../constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../constants/schemas/3p-config';
import type { ResultClickedCallback } from '../../../constants/types';
import { getIntegrationKey } from '../../../utils/get-integration-key';
import { getProductFromProviderId } from '../../../utils/get-product-from-provider-id';
import { enrichExcerpt } from '../../../utils/search-result-utils';
import { IconFor3P } from '../../icon-for-3p';
import { LastModified } from '../../last-modified';
import { messages, type SearchResultTypeMessageKey } from '../../search-result-type/messages';
import { LinkedResultMetadata } from '../linked-result-meta';
import { LinkedResultTitle } from '../linked-result-title';

/**
 * Returns the appropriate icon for linked results based on the category.
 *
 * @param category - The linked result category ('MENTIONS' or 'MENTIONEDBY')
 * @param productKey - The product key for 3P integrations
 * @param resultAri - The parsed ARI object containing resource information
 * @returns React component for the icon or null
 */
const getLinkedResultIcon = (
	category: SearchLinkedResultCategory | undefined,
	productKey3P: string | undefined,
	resultAri: any,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null,
): React.ReactNode => {
	// When 1P mentions 3P: category is 'MENTIONS'
	// Return the correct 3P icon for the linked result
	if (category?.toLocaleUpperCase() === 'MENTIONS' && thirdPartyConfigs) {
		return (
			<IconFor3P
				{...{
					product: productKey3P,
					resourceType: resultAri.resourceType,
					size: 'xxsmall',
					thirdPartyConfigs,
				}}
			/>
		);
	}
	// When 3P is mentionedBy 1P: category is 'MENTIONEDBY'
	// Return the correct 1P icon for the linked result
	else if (category?.toLocaleUpperCase() === 'MENTIONEDBY') {
		const icon1P =
			resultAri.resourceOwner in products1P
				? products1P[resultAri.resourceOwner as keyof typeof products1P].Icon
				: undefined;

		return icon1P ? <Box>{React.createElement(icon1P, { size: 'xxsmall' })}</Box> : null;
	} else if (category?.toLocaleUpperCase() === 'SIMILAR') {
		if (productKey3P && thirdPartyConfigs) {
			return (
				<IconFor3P
					{...{
						product: productKey3P,
						resourceType: resultAri.resourceType,
						size: 'xxsmall',
						thirdPartyConfigs,
					}}
				/>
			);
		} else {
			return <ConfluenceIcon size="xxsmall" shouldUseNewLogoDesign />;
		}
	}
	// Confluence directory or empty file children: category is 'CHILD'
	else if (category?.toLocaleUpperCase() === 'CHILD') {
		if (productKey3P && thirdPartyConfigs) {
			return (
				<IconFor3P
					{...{
						product: productKey3P,
						resourceType: resultAri.resourceType,
						size: 'xxsmall',
						thirdPartyConfigs,
					}}
				/>
			);
		}
		return <ConfluenceIcon size="xxsmall" shouldUseNewLogoDesign />;
	}
	return null;
};

type TypeLabelMessage = (typeof messages)[keyof typeof messages];

/**
 * Returns the message for the type label: linked result's type/subtype when present and
 * recognised, otherwise parent type, otherwise 'unrecognised'.
 *
 * @param category - The linked result category (e.g. MENTIONS, MENTIONEDBY, SIMILAR, CHILD)
 * @param subtype - The linked result subtype (used when category is MENTIONS; for SIMILAR, used when type is absent or unrecognised)
 * @param type - The linked result type (used when category is not MENTIONS; for SIMILAR, tried first before subtype)
 * @param fallbackType - Fallback type when linked result has no recognised type
 * @returns The message descriptor for the type label (use with formatMessage)
 */
export const getTypeLabelMessage = (
	category: SearchLinkedResultCategory | undefined,
	subtype: string | undefined | null,
	type: string | undefined | null,
	fallbackType: SearchResultTypeMessageKey | undefined,
): TypeLabelMessage => {
	const normCategory = category?.toLocaleUpperCase();

	// MENTIONS: use subtype, then fallbackType, then unrecognised
	if (normCategory === 'MENTIONS') {
		if (subtype != null && subtype in messages) {
			return messages[subtype as keyof typeof messages];
		}
		if (fallbackType && fallbackType in messages) {
			return messages[fallbackType as keyof typeof messages];
		}
		return messages.unrecognised;
	}

	// SIMILAR: use type, then subtype, then fallbackType, then unrecognised
	if (normCategory === 'SIMILAR') {
		if (type != null && type in messages) {
			return messages[type as keyof typeof messages];
		}
		if (subtype != null && subtype in messages) {
			return messages[subtype as keyof typeof messages];
		}
		if (fallbackType && fallbackType in messages) {
			return messages[fallbackType as keyof typeof messages];
		}
		return messages.unrecognised;
	}

	// MENTIONEDBY, CHILD, undefined: use type, then fallbackType, then unrecognised
	if (type != null && type in messages) {
		return messages[type as keyof typeof messages];
	}
	if (fallbackType && fallbackType in messages) {
		return messages[fallbackType as keyof typeof messages];
	}
	return messages.unrecognised;
};

type LinkedResultProps = {
	sublistPosition: number;
	category?: SearchLinkedResultCategory;
	linkedResult: SearchLinkedResult;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null;
	onResultClicked?: ResultClickedCallback;
	onResultContextMenu?: ResultClickedCallback;
	parentPosition?: number;
	product?: string;
	/** Fallback type when linked result has no recognised type (must be a key from search-result-type messages) */
	fallbackType?: SearchResultTypeMessageKey;
};

const styles = cssMap({
	iconArea: {
		gridArea: 'icon',
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'center',
		width: '16px',
	},
	newIcon: {
		paddingTop: token('space.025'),
	},
	grid: {
		gridTemplateColumns: 'auto 1fr',
		gridTemplateAreas: `
			'icon heading'
			'icon subheading'
		`,
		columnGap: token('space.050'),
		rowGap: token('space.050'),
		paddingLeft: token('space.150'),
	},
});

export const LinkedResult = ({
	sublistPosition,
	linkedResult,
	category,
	thirdPartyConfigs,
	onResultClicked,
	onResultContextMenu,
	fallbackType,
}: LinkedResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const containerRef = useRef<HTMLDivElement | null>(null);
	const {
		id,
		title,
		url,
		providerId,
		integrationId,
		lastModifiedDate,
		subtype,
		type,
		videoDurationLabel,
	} = linkedResult;

	const resultAri = AnyAri.parse(id);
	const product = getProductFromProviderId(thirdPartyConfigs, providerId);
	const integrationKey = getIntegrationKey(integrationId);
	const thirdPartyProduct = product || integrationKey;
	const firstPartyProduct = resultAri.resourceOwner;

	const productKey = thirdPartyProduct || firstPartyProduct;

	const icon = getLinkedResultIcon(category, thirdPartyProduct, resultAri, thirdPartyConfigs);
	const typeLabel = formatMessage(getTypeLabelMessage(category, subtype, type, fallbackType));

	// Show the video length (e.g. "8 min") after last-modified for linked results carrying a
	// duration label (MS Teams past meeting occurrences).
	const metadataParts: [string, React.ReactNode][] = [
		['type', <>{typeLabel}</>],
		['last-modified', <LastModified lastModified={lastModifiedDate} />],
	];

	if (videoDurationLabel) {
		metadataParts.push(['video-duration', <>{videoDurationLabel}</>]);
	}

	return (
		<Box ref={containerRef}>
			<Grid xcss={styles.grid}>
				<Box xcss={styles.iconArea}>
					<Box xcss={styles.newIcon}>{icon}</Box>
				</Box>
				<LinkedResultTitle
					sublistPosition={sublistPosition}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					category={category}
					provider={productKey}
					id={id}
					title={enrichExcerpt(title)}
					url={url}
				/>
				<LinkedResultMetadata
					searchResultId={id}
					parts={metadataParts}
					containerRef={containerRef}
				/>
			</Grid>
		</Box>
	);
};
